import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Text,
  Group,
  Flex,
  Badge,
  Paper,
  Table,
  Button,
  Skeleton,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { IconChevronUp, IconChevronDown, IconSelector, IconSearch, IconX } from '@tabler/icons-react';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';
import { notifications } from '@mantine/notifications';

export type AggregateKeywordParams = {
  visiopt_campaign_id: string[];
  adset_id: string;
  adset_name: string;
  report_time: string;
  ad_account_id?: string[];
};

type KeywordRow = {
  keyword: string;
  match_type: string;
  impressions: number;
  clicks: number;
  spend: number;
};

type SortKey = keyof KeywordRow;

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'keyword', label: 'Keyword', numeric: false },
  { key: 'match_type', label: 'Match Type', numeric: false },
  { key: 'impressions', label: 'Impressions', numeric: true },
  { key: 'clicks', label: 'Clicks', numeric: true },
  { key: 'spend', label: 'Spend', numeric: true },
];

export function AggregateKeywordDrawer({
  opened,
  onClose,
  params,
  onExport,
}: {
  opened: boolean;
  onClose: () => void;
  params: AggregateKeywordParams | null;
  onExport?: () => Promise<string>;
}) {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';
  const surfaceBg     = isDark ? theme.colors.dark[6] : theme.colors.gray[0];
  const rowBorder     = isDark ? theme.colors.dark[5] : theme.colors.gray[2];
  const headerLabelCo = isDark ? theme.colors.dark[1] : theme.colors.gray[6];
  const numericCo     = isDark ? theme.colors.gray[3] : theme.colors.gray[8];

  const [keywords, setKeywords] = useState<KeywordRow[]>([]);
  const [loading, setLoading] = useState(false);
  // Reopening fast must not let an earlier response overwrite the current one.
  const requestSeq = useRef(0);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [exporting, setExporting] = useState(false);

  // Re-fetch whenever a different ad group or date range is opened, never on a plain re-render.
  const openKey = opened && params ? `${params.adset_id}|${params.report_time}` : '';

  useEffect(() => {
    if (openKey === '' || !params) { return; }
    setKeywords([]);
    setSortKey(null);
    setSortDir('asc');
    setLoading(true);
    const seq = ++requestSeq.current;
    AggregatedReportsAPI
      .getkeyworddrawerdata({
        visiopt_campaign_id: params.visiopt_campaign_id,
        adset_id: params.adset_id,
        adset_name: params.adset_name,
        report_time: params.report_time,
        ad_account_id: params.ad_account_id ?? [],
      })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (seq !== requestSeq.current) { return; }
        setKeywords(data && data.status === 1 && Array.isArray(data.data?.keyword_data) ? data.data.keyword_data : []);
      })
      .catch((e) => console.log(e))
      .finally(() => { if (seq === requestSeq.current) { setLoading(false); } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  const handleSort = (key: SortKey) => {
    const dir: 'asc' | 'desc' = sortKey === key && sortDir === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDir(dir);
    setKeywords((prev) => [...prev].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        const cmp = String(av ?? '').localeCompare(String(bv ?? ''));
        return dir === 'asc' ? cmp : -cmp;
      }
      return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
    }));
  };

  // Totals let the drawer be checked against the ad set row without adding keywords by hand.
  const totals = keywords.reduce(
    (acc, row) => {
      acc.impressions += Number(row.impressions) || 0;
      acc.clicks += Number(row.clicks) || 0;
      acc.spend += Number(row.spend) || 0;
      return acc;
    },
    { impressions: 0, clicks: 0, spend: 0 }
  );

  // Every Keyword View exports the same complete keyword file for the report, not just this view.
  const exportKeywords = () => {
    if (!onExport) { return; }
    setExporting(true);
    onExport()
      .then((fileName) => {
        if (fileName === '') {
          notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'No keyword data available to export.' });
        }
      })
      .catch(() => {
        notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'Could not export the keywords.' });
      })
      .finally(() => setExporting(false));
  };

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) { return <IconSelector size={13} stroke={1.6} color={headerLabelCo} />; }
    return sortDir === 'asc'
      ? <IconChevronUp size={13} stroke={2} color={theme.colors.teal[6]} />
      : <IconChevronDown size={13} stroke={2} color={theme.colors.teal[6]} />;
  };

  return (
    <Drawer.Root opened={opened} onClose={onClose} position="right" size="xl" trapFocus={false}>
      <Drawer.Overlay />
      <Drawer.Content>
        <Drawer.Header sx={{ paddingBottom: 12, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Group spacing={8} mb={2} noWrap>
              <Text size="lg" weight={700} sx={{ lineHeight: 1.2 }}>Keyword View</Text>
              {params && (
                <Badge size="md" variant="light" color="blue" sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {params.adset_name}
                </Badge>
              )}
            </Group>
            <Text size="sm" color="dimmed" sx={{ wordBreak: 'break-word' }}>
              Google ad group keywords · {keywords.length.toLocaleString()} keyword
              {keywords.length === 1 ? '' : 's'} in the report's date range
            </Text>
          </Box>
          <Group spacing="xs" noWrap>
            <Button size="xs" variant="light" color="teal" onClick={exportKeywords} disabled={!onExport} loading={exporting}>
              Export
            </Button>
            <Drawer.CloseButton />
          </Group>
        </Drawer.Header>

        <Drawer.Body sx={{ paddingTop: 0 }}>
          <Paper
            withBorder
            radius="md"
            sx={{ backgroundColor: 'transparent', borderColor: rowBorder, overflow: 'hidden' }}
          >
            {loading ? (
              <Flex direction="column" gap="xs" p="sm">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} height={34} radius="sm" />
                ))}
              </Flex>
            ) : keywords.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={48}>
                <IconSearch size="2rem" stroke={1.4} color={headerLabelCo} />
                <Text size="sm" mt={8} color="dimmed">No keywords found for this ad group.</Text>
              </Flex>
            ) : (
              <SimpleBar autoHide={false} style={{ maxHeight: 'calc(100vh - 210px)' }}>
                <Table
                  withColumnBorders
                  sx={{ marginBottom: 0, 'th, td': { borderColor: `${rowBorder} !important` } }}
                >
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: surfaceBg, zIndex: 1 }}>
                    <tr>
                      {COLUMNS.map((c) => (
                        <th
                          key={c.key}
                          style={{ padding: '10px 12px', whiteSpace: 'nowrap', textAlign: c.numeric ? 'right' : 'left' }}
                        >
                          <UnstyledButton onClick={() => handleSort(c.key)} sx={{ width: '100%' }}>
                            <Group spacing={4} align="center" position={c.numeric ? 'right' : 'left'} noWrap>
                              <Text size="xs" transform="uppercase" weight={700} sx={{ color: headerLabelCo, letterSpacing: 0.4 }}>
                                {c.label}
                              </Text>
                              <SortIcon column={c.key} />
                            </Group>
                          </UnstyledButton>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.map((row, i) => (
                      <tr key={`${row.keyword}-${row.match_type}-${i}`} style={{ borderTop: `1px solid ${rowBorder}` }}>
                        <td style={{ padding: '10px 12px' }}>
                          <Text size="sm" sx={{ overflowWrap: 'anywhere' }}>{row.keyword || '-'}</Text>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <Badge size="sm" variant="light" color="gray" radius="sm" sx={{ textTransform: 'none', fontWeight: 600 }}>
                            {row.match_type || '-'}
                          </Badge>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>
                            {Number(row.impressions || 0).toLocaleString()}
                          </Text>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>
                            {Number(row.clicks || 0).toLocaleString()}
                          </Text>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>
                            ${Number(row.spend || 0).toFixed(2)}
                          </Text>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${rowBorder}`, backgroundColor: surfaceBg }}>
                      <td style={{ padding: '10px 12px' }}>
                        <Text size="sm" weight={700}>Total</Text>
                      </td>
                      <td style={{ padding: '10px 12px' }} />
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {totals.impressions.toLocaleString()}
                        </Text>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {totals.clicks.toLocaleString()}
                        </Text>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          ${totals.spend.toFixed(2)}
                        </Text>
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </SimpleBar>
            )}
          </Paper>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}
